import { Injectable } from '@angular/core';
import { Home } from '@library/store/homes/homes.interface';
import { EnumMeasureTypes } from '@library/store/measures/measures.interface';
import { Module } from '@library/store/modules/modules.interface';
import { Room } from '@library/store/rooms/rooms.interface';
import { ComponentStore } from '@ngrx/component-store';
import { ControlPageCompareService } from '@pages/control-page/control-page-main/control-page-compare/control-page-compare.service';
import { CpCompareSidebarColorsService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar-colors.service';
import { firstValueFrom } from 'rxjs';
import { CPCompareSidebarCacheService } from './cp-compare-sidebar-cache.service';

export const enum YScaleType {
  TEMPERATURE = 'y-temperature',
  WATER = 'y-water',
  GAS = 'y-gas',
  PRICE = 'y-price',
  SPEED = 'y-speed',
  PRESSURE = 'y-pressure',
  PERCENTAGE = 'y-percentage',
  NOISE = 'y-noise',
  RAIN = 'y-rain',
  PPM = 'y-ppm',
  ELECTRICITY = 'y-electricity',
  WIND_ANGLE = 'y-wind-angle',
}

export interface MeasureCard {
  label: string;
  sidebarLabel: string;
  icon: string;
  module?: Module;
  home?: Home;
  room?: Room;
  type: 'bar' | 'line' | 'custom-bar';
  measureType: EnumMeasureTypes;
  yScaleType: YScaleType,
  color?: string,
}

interface CompareSidebarState {
  maxCards: number,
  measureCards: MeasureCard[];
}

@Injectable()
export class CpCompareSidebarService extends ComponentStore<CompareSidebarState> {

  constructor(
    private controlPageCompareService: ControlPageCompareService,
    private cpCompareCacheService: CPCompareSidebarCacheService,
    private compareSidebarColorsService: CpCompareSidebarColorsService,
  ) {
    super({
      maxCards: 4,
      measureCards: [],
    });
  }

  readonly measureCards$ = this.select(state => state.measureCards);

  readonly selectCard = this.updater((state, measureCard: MeasureCard) => {
    this.controlPageCompareService.loadMeasures([measureCard]);
    let measureCards = [...state.measureCards];

    // When we reach the maximum card limit, we need to unselect the oldest one
    if (measureCards.length === state.maxCards) {
      const cardToRemove = measureCards[0];
      measureCards = this.removeMeasureCard(measureCards, cardToRemove);
    }

    const color = this.compareSidebarColorsService.getColor(measureCard);
    measureCard.color = color;

    measureCards.push(measureCard);

    this.cpCompareCacheService.setDataset(measureCard);

    return {
      ...state,
      measureCards: measureCards,
    };
  });

  readonly unSelectCard = this.updater((state, measureCard: MeasureCard) => {

    const measureCards = this.removeMeasureCard(state.measureCards, measureCard);

    return {
      ...state,
      measureCards: measureCards,
    };
  });

  readonly setMaxCards = this.updater((state, maxCards: number) => {
    // If the number of measure cards is greater than the new maximum
    // we need to unselect the oldest cards
    let measureCards = [...state.measureCards];
    while (measureCards.length > maxCards) {
      measureCards = this.removeMeasureCard(measureCards, measureCards[0]);
    }
    return {
      ...state,
      measureCards: measureCards,
      maxCards: maxCards,
    };
  });

  private removeMeasureCard(measureCards: MeasureCard[], measureCard: MeasureCard) {
    this.compareSidebarColorsService.releaseColor(measureCard);
    this.cpCompareCacheService.removeDataset(measureCard);
    const getId = (card: MeasureCard) => (card.home ?? card.room ?? card.module).id + card.measureType;
    return [...measureCards.filter(card => getId(card) !== getId(measureCard))];
  }

  /**
   * Selects the measure types selected when entering the webapp
   * or when changing home.
   *
   * Selects the last selected cards in previous session
   *
   * if it's the first visite, do not selct anything (TODO wait for PM approvals)
   *
   */
  selectCards(measureCards: MeasureCard[]) {
    measureCards.forEach(card => this.selectCard(card));
  }

  async resetMeasureCards() {
    const measureCards = await firstValueFrom(this.measureCards$);
    measureCards.forEach(card => this.unSelectCard(card));
  }

}
